import axios, { AxiosInstance, type InternalAxiosRequestConfig } from 'axios';
import {
  useAuthStore,
  readPersistedAccessToken,
  readPersistedRefreshToken,
} from '../stores/authStore';
import type { AuthUser } from '../types/auth';
import { showIn, type Language } from '../i18n/language';
import { emailFromAccessToken } from '../lib/accessToken';
import { API_URL } from '../lib/config';
import { optionalGeolocationHeader } from '../lib/geolocation';
import { inactivity } from '../lib/inactivity';

// Paths that must NEVER be hit by the auto-redirect when a refresh
// fails: they're either pre-auth screens (so a bounce would be a
// loop) or carry a single-use token that the redirect would discard.
const _UNAUTH_PATHS = ['/login', '/auth/', '/oauth/callback', '/reset-password', '/accept-invitation'];

function _onAuthPath(): boolean {
  const here = globalThis.location.pathname;
  return _UNAUTH_PATHS.some((p) => here.startsWith(p));
}

/** A refresh that was not sent because the person has done nothing since the last one. A
 *  session is kept alive by the person, never by background traffic; the server refuses the
 *  refresh of a session that has been idle too long, and this keeps the SPA from asking. */
export class RefreshWithheld extends Error {
  constructor() {
    super('refresh_withheld');
    this.name = 'RefreshWithheld';
  }
}

/** Runs `work` alone across all tabs of the browser: two tabs presenting the same refresh token
 *  would be a replay, which the server answers by ending every session of the account. */
async function underRefreshLock<T>(work: () => Promise<T>): Promise<T> {
  const locks = globalThis.navigator?.locks;
  return locks ? await locks.request('qmi.auth.refresh', work) : work();
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  role: string;
  must_change_password?: boolean;
  /** When true, the user must enrol a second factor before proceeding
   *  (new with the MFA epic). Carried into the store user so the route
   *  guard can pin the user on /auth/mfa-enroll. */
  must_enroll_mfa?: boolean;
}

/** The alternate `/auth/login` outcome for an already-enrolled user: no
 *  tokens are issued yet — the caller must complete a second-factor
 *  challenge via `verifyMfa` to receive the Token. Distinguished from a
 *  `TokenResponse` by the presence of `mfa_required`. `/auth/oauth/exchange`
 *  answers with the same shape when a provider sign-in does not stand in
 *  for the account's own second factor. */
export interface MfaChallengeResponse {
  mfa_required: true;
  /** Single-use, short-lived token that ties the second-factor step to
   *  this login attempt. Component-local only — NEVER persist it. */
  mfa_token: string;
  /** Factors the user has available, in the order the backend prefers. */
  methods: MfaMethod[];
}

export type MfaMethod = 'totp' | 'recovery_code' | 'webauthn';

/** Discriminated result of `login()` and of a successful
 *  `exchangeOAuthCode()`: either a full session (tokens) or a pending
 *  second-factor challenge. Narrow with `isMfaChallenge`. */
export type LoginResult = TokenResponse | MfaChallengeResponse;

/** Body for POST /auth/mfa/verify. `code` for totp/recovery_code,
 *  `assertion` (serialized WebAuthn credential) for webauthn. */
export interface VerifyMfaPayload {
  mfa_token: string;
  method: MfaMethod;
  code?: string;
  assertion?: Record<string, unknown>;
}

/** A registered passkey as listed by GET /auth/webauthn/credentials. */
export interface WebAuthnCredential {
  id: string;
  label: string;
  aaguid: string | null;
  last_used_at: string | null;
  created_at: string;
}

/** Type guard: is this sign-in result a pending second-factor challenge? */
export function isMfaChallenge(result: LoginResult): result is MfaChallengeResponse {
  return (result as MfaChallengeResponse).mfa_required === true;
}

/** A way to sign in besides email and password, as listed by
 *  GET /auth/oauth/providers. `name` is what the button says after
 *  "Continue with". */
export interface SignInProvider {
  id: string;
  name: string;
}

/** Keep the well-formed entries of a provider list and drop the rest. The
 *  list is rendered on the login page, so an answer that is not a list (a
 *  proxy's HTML page, an older backend) must come out empty, not crash it. */
function toSignInProviders(data: unknown): SignInProvider[] {
  if (!Array.isArray(data)) return [];
  const providers: SignInProvider[] = [];
  for (const entry of data as unknown[]) {
    const { id, name } = (entry ?? {}) as Partial<SignInProvider>;
    if (typeof id === 'string' && id && typeof name === 'string' && name) {
      providers.push({ id, name });
    }
  }
  return providers;
}

/** Does this exchange answer carry a usable session? Anything that is neither
 *  this nor a second-factor challenge is treated as a failed exchange. */
function isTokenResponse(data: unknown): data is TokenResponse {
  const token = (data ?? {}) as Partial<TokenResponse>;
  return (
    typeof token.access_token === 'string' && token.access_token !== ''
    && typeof token.refresh_token === 'string' && token.refresh_token !== ''
    && typeof token.role === 'string'
  );
}

interface LoginCredentials {
  email: string;
  password: string;
}

export interface CurrentUserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  phone: string | null;
  timezone: string | null;
  /** The language the person chose for the screens. */
  language?: Language;
  role: string;
  is_active: boolean;
  must_change_password: boolean;
  /** Backend-driven: the user still owes a second-factor enrolment.
   *  Cleared once the enrolment ceremony completes. */
  must_enroll_mfa: boolean;
  /** A client owes the acceptance of the privacy notice in force (#289). */
  privacy_notice_owed?: boolean;
  /** A client owes the acceptance of the Terms and Conditions in force (0181). */
  terms_owed?: boolean;
  /** Latest non-archived onboarding-case state slug (e.g. 'pending_profile',
   *  'under_review', 'approved'). null for staff or clients with no case
   *  yet. Used to gate client-only post-KYC pages. */
  client_onboarding_state: string | null;
  /** Compliance hold (0038): 'suspended' renders the neutral read-only
   *  banner; the backend 403s every mutating call. Always null for staff.
   *  The cause is never exposed. */
  suspension_status: string | null;
  /** The client reported a change as not theirs (0079): money-out and account changes are frozen. */
  security_hold?: boolean;
}

/** What the auth store keeps of a profile read from /auth/me. */
export function authUserFromProfile(profile: CurrentUserProfile): AuthUser {
  return {
    email: profile.email,
    role: profile.role,
    first_name: profile.first_name,
    last_name: profile.last_name,
    must_change_password: profile.must_change_password,
    must_enroll_mfa: profile.must_enroll_mfa,
    privacy_notice_owed: profile.privacy_notice_owed === true,
    terms_owed: profile.terms_owed === true,
  };
}

export interface ChangePasswordPayload {
  current_password: string;
  new_password: string;
}

export interface MfaStatus {
  /** Whether the user has a CONFIRMED TOTP enrolment. */
  enrolled: boolean;
  /** How many single-use recovery codes are still unused. `null` when the
   *  user has never enrolled (no code set was ever issued). */
  recovery_codes_remaining: number | null;
}

export interface MfaSetup {
  /** `otpauth://` URI for manual entry into an authenticator app. */
  provisioning_uri: string;
  /** That URI rendered as an inline PNG data-URI for the QR <img>. */
  qr_data_uri: string;
}

/** Result of POST /auth/mfa/confirm and .../recovery-codes/regenerate. The
 *  `recovery_codes` plaintext is shown to the user exactly ONCE — it is not
 *  retrievable afterwards, only regenerable. */
export interface MfaConfirmResult {
  enrolled: boolean;
  recovery_codes: string[];
  recovery_codes_remaining: number;
}

/** Sparse payload for PATCH /auth/me. Add new updatable profile
 *  fields here as they become editable — the backend applies only
 *  the keys present in the request body. */
export interface UpdateMePayload {
  // No `phone`: since migration 0076 the API refuses it (422
  // phone_requires_verification). A number reaches the account only by being
  // proven — see requestPhoneCode / confirmPhoneCode below.
  timezone?: string;
  language?: Language;
}

/** GET /auth/phone/verification. The number is masked even for its owner. */
export interface PhoneVerificationStatus {
  phone_masked: string | null;
  verified: boolean;
  verified_at: string | null;
  verified_via: string | null;
}

/** One open session of the caller, as GET /auth/sessions lists it: facts only. */
export interface ActiveSession {
  id: string;
  /** "Chrome on Windows"; "Unknown browser" when the browser cannot be told. */
  device_label: string;
  /** The network operator's name, or null when it is not known. */
  network_name: string | null;
  started_at: string;
  last_seen_at: string;
  /** True for the session this request was made from ("This device"). */
  is_current: boolean;
}

export interface PhoneCodeSent {
  sent_to: string;
  channel: string;
  expires_in_seconds: number;
}

/** A request the API refused, as axios hands it to the response interceptor. */
interface RefusedRequest {
  config?: InternalAxiosRequestConfig & { _retry?: boolean };
  response?: { status?: number; data?: { detail?: unknown } };
}

/** The code the server named in an error's `detail`, when it named one. */
function refusalCode(detail: unknown): string | null {
  if (typeof detail !== 'object' || detail === null) return null;
  const code = (detail as { code?: unknown }).code;
  return typeof code === 'string' ? code : null;
}

/** Raises in the session the obligation the server says the account still owes.
 *  Answers whether the code was one. */
function raiseOwedObligation(code: string | null): boolean {
  if (
    code !== 'password_change_required' &&
    code !== 'mfa_enrolment_required' &&
    code !== 'privacy_notice_acceptance_required' &&
    code !== 'terms_acceptance_required'
  ) {
    return false;
  }
  const { user, setUser } = useAuthStore.getState();
  if (user) {
    if (code === 'password_change_required') setUser({ ...user, must_change_password: true });
    else if (code === 'mfa_enrolment_required') setUser({ ...user, must_enroll_mfa: true });
    else if (code === 'terms_acceptance_required') setUser({ ...user, terms_owed: true });
    else setUser({ ...user, privacy_notice_owed: true });
  }
  return true;
}

/** Whether a refusal means the session is no longer valid.
 *
 *  · 401 — a token was sent and is invalid, expired or revoked. Except when the 401
 *    names a second-factor refusal (a wrong or already-used code): that is the
 *    server's answer about the code, and refreshing and sending the same code again
 *    would count one mistake twice toward the lock.
 *  · 403 "Not authenticated" — no token at all; FastAPI's HTTPBearer answers 403
 *    when the Authorization header is missing. The person has no credentials, not
 *    insufficient ones. */
function isSessionFailure(status: number | undefined, detail: unknown): boolean {
  if (status === 403) return detail === 'Not authenticated';
  if (status !== 401) return false;
  return !refusalCode(detail)?.startsWith('mfa_');
}

class AuthService {
  private readonly api: AxiosInstance;

  constructor() {
    this.api = axios.create({
      baseURL: API_URL,
      // Without a timeout a hung request never settles, so a screen that waits
      // on it waits forever — which is how the client portfolio dashboard could
      // sit on "Loading…" with no error and no recourse (error audit,
      // 2026-09-11). 30s is generous for every call we make: the slowest are
      // statement generation and the funds penalty quote, both well under it.
      timeout: 30_000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Attach the bearer token. We deliberately use the sync helper
    // (reads the persist envelope direct from localStorage) instead
    // of `useAuthStore.getState().accessToken` because the first
    // API call after page load can fire BEFORE zustand's persist
    // middleware finishes hydrating (hydration runs in a microtask
    // even for sync storage in zustand v4.5+). The persist envelope
    // IS the single source of truth — this is just a sync reader
    // path for the same data. No drift possible.
    this.api.interceptors.request.use((config) => {
      const token = readPersistedAccessToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    });

    this.api.interceptors.response.use(
      (response) => response,
      (error: RefusedRequest) => this.answerRefusal(error),
    );
  }

  /** What to do with a request the API refused.
   *
   *  · The account still owes a password change or a staff second-factor enrolment
   *    (403 with that code): the route guard normally sends the person there before
   *    any call is made; if a call gets through first, the same flag is raised so the
   *    guard does it now.
   *  · The session is no longer valid (see `isSessionFailure`): it is refreshed once
   *    and the request sent again.
   *  · Anything else is the caller's to show. */
  private async answerRefusal(error: RefusedRequest): Promise<unknown> {
    const status = error.response?.status;
    const detail = error.response?.data?.detail;
    if (status === 403 && raiseOwedObligation(refusalCode(detail))) throw error;
    if (!isSessionFailure(status, detail) || error.config?._retry) throw error;
    return this.sendAgainWithAFreshSession(error);
  }

  private async sendAgainWithAFreshSession(error: RefusedRequest): Promise<unknown> {
    const request = error.config;
    if (request) request._retry = true;

    // No refresh token to try → straight to the redirect.
    // Sync read (same reasoning as the request interceptor).
    if (!readPersistedRefreshToken()) {
      this.handleSessionExpired();
      throw error;
    }

    let tokens: TokenResponse;
    try {
      tokens = await this.refreshToken();
    } catch (refreshError) {
      if (refreshError instanceof RefreshWithheld) {
        // Nobody has touched the screen since the last refresh, so this request does not
        // renew the session: it keeps its answer, and the session ends when the inactivity
        // limit does. If another tab renewed the token meanwhile, the request goes again with it.
        const renewed = readPersistedAccessToken();
        if (!request || !renewed || request.headers.Authorization === `Bearer ${renewed}`) {
          throw error;
        }
        request.headers.Authorization = `Bearer ${renewed}`;
        return this.api(request);
      }
      // Both tokens are dead (expired, revoked via rotation or replay detection, or
      // the person was forced out by an admin). The session is torn down and the
      // page goes to /login, instead of showing "Not authenticated" messages
      // scattered over the screen that was open.
      this.handleSessionExpired();
      throw refreshError;
    }
    if (!request) throw error;
    request.headers.Authorization = `Bearer ${tokens.access_token}`;
    return this.api(request);
  }

  /** Centralised "your session is over" handler. Called from the
   *  axios interceptor when refresh fails AND from any code path
   *  that decides authentication is no longer valid. Idempotent. */
  private handleSessionExpired(): void {
    this.logout();
    // Hard-nav so any React component currently rendering a 401
    // error message gets unmounted before the user notices. Skip
    // when we're already on an auth surface to avoid a redirect
    // loop (e.g. the /login page itself failing /auth/me).
    if (!_onAuthPath()) {
      globalThis.location.assign('/login?reason=session_expired');
    }
  }

  async login(credentials: LoginCredentials): Promise<LoginResult> {
    // OAuth2PasswordRequestForm expects application/x-www-form-urlencoded.
    // Using URLSearchParams + explicit header overrides the axios default
    // 'application/json' set in the constructor.
    const body = new URLSearchParams();
    body.append('username', credentials.email);
    body.append('password', credentials.password);

    const response = await this.api.post<LoginResult>(
      '/api/v1/auth/login',
      body,
      { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
    );
    // An already-enrolled user gets an MfaChallenge (NO tokens) — the
    // caller must complete `verifyMfa` to receive a Token. We must NOT
    // call setSession here: there's no access_token, and writing an
    // authenticated store off a challenge would grant an unverified
    // session. Return the challenge so the Login page switches to its
    // second-factor step.
    if (isMfaChallenge(response.data)) {
      return response.data;
    }
    const token = response.data;
    // Persist the full session through the single store. The axios
    // interceptor reads `accessToken` from here on every request, so
    // the next API call after `login()` resolves is already
    // authenticated. The OAuth path (`exchangeOAuthCode`) MUST leave
    // the store in the same shape — both login methods share this
    // contract.
    this.persistToken(token, credentials.email);
    return token;
  }

  /** Shared store-write for a Token, however it was obtained: password
   *  login, `verifyMfa` or `exchangeOAuthCode`. Keeps the exact store
   *  shape every sign-in depends on: {accessToken, refreshToken,
   *  user:{email, role, must_change_password, must_enroll_mfa}}.
   *
   *  `email` is what the person typed. A sign-in through another account
   *  has none, so the address is then read from the token itself: the
   *  right account shows at once, before /auth/me fills in the rest. */
  private persistToken(token: TokenResponse, email: string): void {
    inactivity.markSignedIn();
    useAuthStore.getState().setSession({
      accessToken: token.access_token,
      refreshToken: token.refresh_token,
      user: {
        email: email || emailFromAccessToken(token.access_token),
        role: token.role,
        must_change_password: Boolean(token.must_change_password),
        must_enroll_mfa: Boolean(token.must_enroll_mfa),
      },
    });
  }

  /** Complete a second-factor challenge started by `login()`. On success
   *  the backend returns a Token, which we persist through the single
   *  store exactly like a direct login. The mfa_token is single-use and
   *  never touches localStorage — the caller holds it in component state.
   *  Errors surface `detail:{code,message}` (mfa_invalid_code,
   *  mfa_code_replayed, mfa_too_many_attempts, mfa_challenge_invalid,
   *  mfa_unavailable, webauthn_assertion_required). */
  async verifyMfa(payload: VerifyMfaPayload, email = ''): Promise<TokenResponse> {
    const res = await this.api.post<TokenResponse>('/api/v1/auth/mfa/verify', payload);
    this.persistToken(res.data, email);
    return res.data;
  }

  /** Fetch the `PublicKeyCredentialRequestOptions` (base64url JSON) for a
   *  passkey login. Feed the result through `preformatRequestOptions`
   *  before `navigator.credentials.get()`. */
  async getWebAuthnLoginOptions(mfaToken: string): Promise<Record<string, unknown>> {
    const res = await this.api.post<Record<string, unknown>>(
      '/api/v1/auth/mfa/webauthn/options',
      { mfa_token: mfaToken },
    );
    return res.data;
  }

  // NOTE — `register()` was removed 2026-05-11. Public registration
  // is a back door in this product: clients enter via the bulk-invite
  // flow (`/auth/accept-invitation` consumes the one-time token from
  // the email) and staff is created via `/admin/staff`. There is no
  // self-service signup. See memory `invitations_client_only.md`.

  private _refreshInflight: Promise<TokenResponse> | null = null;

  /** Trades the refresh token for a new pair and keeps it. One at a time: a screen
   *  sends several requests at once, and when the access token has just expired each
   *  of them is refused. Each presenting the same refresh token would be one rotation
   *  and then replays of a token already used, which the server answers by ending
   *  every session of the account. Everyone who asks while one is under way gets its
   *  answer. */
  refreshToken(): Promise<TokenResponse> {
    this._refreshInflight ??= this.rotateTokens().finally(() => {
      this._refreshInflight = null;
    });
    return this._refreshInflight;
  }

  private rotateTokens(): Promise<TokenResponse> {
    // One tab at a time. What is decided below is decided inside the lock, so a tab that
    // waited sees the refresh the other one just made.
    return underRefreshLock(() => this.rotateTokensAlone());
  }

  private async rotateTokensAlone(): Promise<TokenResponse> {
    // Sync read — same race-avoidance reasoning as the request
    // interceptor. This runs inside the response interceptor's retry
    // path, which can fire before zustand hydration completes.
    const refreshToken = readPersistedRefreshToken();
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }
    if (!inactivity.mayRefresh()) throw new RefreshWithheld();

    const startedAt = Date.now();
    const { data } = await axios.post<TokenResponse>(
      `${API_URL}/api/v1/auth/refresh`,
      { refresh_token: refreshToken }
    );
    inactivity.markRefreshed(startedAt);
    // The user payload stays: a refresh changes the credentials, not
    // who is signed in, so there is no need to read /auth/me again.
    const existingUser = useAuthStore.getState().user;
    if (existingUser) {
      useAuthStore.getState().setSession({
        accessToken: data.access_token,
        refreshToken: data.refresh_token,
        user: {
          ...existingUser,
          role: data.role,
          must_change_password: Boolean(data.must_change_password),
          must_enroll_mfa: Boolean(data.must_enroll_mfa),
        },
      });
    }
    return data;
  }

  /** Renews the session now if the person has been active since the last renewal, and does
   *  nothing otherwise. A refusal by the server (the session ended, or was idle too long)
   *  signs the person out; a network failure is left for the caller to try again. */
  async refreshWhileActive(): Promise<void> {
    if (!readPersistedRefreshToken()) {
      this.handleSessionExpired();
      return;
    }
    try {
      await this.refreshToken();
    } catch (refreshError) {
      if (refreshError instanceof RefreshWithheld) return;
      const status = (refreshError as { response?: { status?: number } }).response?.status;
      if (status === 401 || status === 403) this.handleSessionExpired();
      else throw refreshError;
    }
  }

  /** The person was away for the whole inactivity limit: the session is ended on the server
   *  (best effort: the page is about to leave, so the request is sent so it survives that) and
   *  here, and the sign-in page says why. */
  signOutForInactivity(): void {
    // What the browser holds now (another tab may have renewed the pair, or ended the session).
    const accessToken = readPersistedAccessToken();
    const refreshToken = readPersistedRefreshToken();
    if (accessToken && refreshToken) {
      fetch(`${API_URL}/api/v1/auth/logout`, {
        method: 'POST',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ refresh_token: refreshToken }),
      }).catch(() => undefined);
    }
    this.clearLocalSession();
    if (!_onAuthPath()) globalThis.location.assign('/login?reason=inactive');
  }

  async forgotPassword(email: string): Promise<{ message: string }> {
    const response = await this.api.post<{ message: string }>(
      '/api/v1/auth/forgot-password',
      { email }
    );
    return response.data;
  }

  async resetPassword(token: string, new_password: string): Promise<{ message: string }> {
    const response = await this.api.post<{ message: string }>(
      '/api/v1/auth/reset-password',
      { token, new_password }
    );
    return response.data;
  }

  /** Redeem an invitation token from the email link and provision the
   *  client account (local password). Names come from the invitation row,
   *  so only the new password is collected here. Returns the new user's
   *  identity; the client then signs in normally. */
  async acceptInvitation(
    token: string,
    new_password: string,
  ): Promise<{ user_id: string; email: string; role: string; message: string }> {
    const response = await this.api.post<{
      user_id: string;
      email: string;
      role: string;
      message: string;
    }>('/api/v1/auth/accept-invitation', {
      token,
      new_password,
    });
    return response.data;
  }

  // In-memory cache for /auth/me. Multiple components mounting in
  // parallel (e.g. portfolio page + topbar both wanting `me.id` for
  // their /clients/{id}/* calls) used to re-hit the endpoint each time;
  // we collapse all concurrent calls onto a single in-flight Promise
  // and serve resolved results from the cache afterwards. `clearMeCache`
  // exists so flows that *mutate* the profile (PATCH /auth/me,
  // change-password) can invalidate it.
  private _meCache: CurrentUserProfile | null = null;
  private _meInflight: Promise<CurrentUserProfile> | null = null;

  /**
   * Returns the authenticated user's slim profile. Used both to surface
   * fields not in the JWT (phone, first_name, last_name) and as the
   * source of the caller's UUID for resource-keyed endpoints like
   * `/agents/{agent_id}/*` and `/clients/{client_id}/*` (which
   * replaced the older `/me/*` aliases).
   */
  async getCurrentUser(forceRefresh = false): Promise<CurrentUserProfile> {
    if (!forceRefresh && this._meCache) return this._meCache;
    if (!forceRefresh && this._meInflight) return this._meInflight;
    this._meInflight = this.api
      .get<CurrentUserProfile>('/api/v1/auth/me')
      .then((res) => {
        this._meCache = res.data;
        this._meInflight = null;
        return res.data;
      })
      .catch((err) => {
        this._meInflight = null;
        throw err;
      });
    return this._meInflight;
  }

  /** Caller's UUID. Cached after the first /auth/me hit. Use this
   *  instead of calling getCurrentUser when you only need the id. */
  async getMyId(): Promise<string> {
    return (await this.getCurrentUser()).id;
  }

  /** Drops the cached /auth/me — call after any flow that mutated the
   *  profile so the next read returns fresh values. */
  private clearMeCache(): void {
    this._meCache = null;
  }

  /** Partial update of the caller's own profile. The backend ignores
   *  any field not present in the payload, so this is the single
   *  entry point for editing timezone and whatever simple
   *  attributes get added to the user record over time. Password
   *  rotation stays on its own method because of the credential-
   *  verification + session-revocation side effects. */
  private async updateMe(payload: UpdateMePayload): Promise<CurrentUserProfile> {
    const res = await this.api.patch<CurrentUserProfile>(
      '/api/v1/auth/me',
      payload,
    );
    this._meCache = res.data;  // refresh the cache with the new state
    return res.data;
  }

  /** The person picked a language: their account keeps it, then the portal is shown in it. */
  async chooseMyLanguage(language: Language): Promise<void> {
    await this.updateMe({ language });
    showIn(language);
  }

  async getPhoneVerification(): Promise<PhoneVerificationStatus> {
    const res = await this.api.get<PhoneVerificationStatus>('/api/v1/auth/phone/verification');
    return res.data;
  }

  /** Ask for a code to be sent to `phone`. The number must carry its country
   *  code (+52 / +1); the account keeps its current number until the new one is
   *  confirmed, so abandoning this strands nobody. */
  async requestPhoneCode(phone: string, mfaCode?: string): Promise<PhoneCodeSent> {
    // `mfa_code` is only needed when this REPLACES a proven number: the server
    // answers 403 mfa_step_up_required and the card asks for it then.
    const res = await this.api.post<PhoneCodeSent>(
      '/api/v1/auth/phone/verification-request',
      mfaCode ? { phone, mfa_code: mfaCode } : { phone },
      { headers: await optionalGeolocationHeader() },
    );
    return res.data;
  }

  /** Confirm the code. On success the server has written the number to the
   *  account, so the cached /auth/me is stale by definition. */
  async confirmPhoneCode(code: string): Promise<void> {
    await this.api.post(
      '/api/v1/auth/phone/verification-confirm',
      { code },
      { headers: await optionalGeolocationHeader() },
    );
    this.clearMeCache();
  }

  /** Browser-detected TZ pushed on first login.
   *
   *  Reads /auth/me first and only PATCHes when `timezone` is still
   *  null — this preserves the "agent admin-set TZ must not be
   *  clobbered by a stale browser TZ from another device" invariant
   *  that used to live server-side on the now-removed
   *  /auth/me/capture-timezone endpoint. PATCH /auth/me allows
   *  overwriting so a future settings page can let the user change
   *  their TZ explicitly. */
  async captureMyTimezone(): Promise<CurrentUserProfile | null> {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return null;
    const me = await this.getCurrentUser();
    if (me.timezone) return me;
    return this.updateMe({ timezone: tz });
  }

  /** What every sign-in does once it holds a session, whichever way it got
   *  one. Neither step may hold the sign-in back, so neither failure is
   *  reported. An account with no timezone takes the browser's, and the
   *  store — which so far holds only what the token says — takes the name
   *  and the flags from /auth/me. Tokens are untouched. */
  hydrateSignedInUser(): void {
    this.captureMyTimezone().catch(() => {});
    this.getCurrentUser()
      .then((profile) => useAuthStore.getState().setUser(authUserFromProfile(profile)))
      .catch(() => {});
  }

  /** Rotate the caller's own password. Required step when the
   *  backend has flagged the account with must_change_password. On
   *  success the flag is cleared on the persisted user so the next
   *  route guard lets the user proceed to their dashboard. */
  async changePassword(payload: ChangePasswordPayload): Promise<CurrentUserProfile> {
    const res = await this.api.post<CurrentUserProfile>(
      '/api/v1/auth/me/password',
      payload,
    );
    const existingUser = useAuthStore.getState().user;
    if (existingUser) {
      useAuthStore.getState().setUser({
        ...existingUser,
        must_change_password: false,
      });
    }
    return res.data;
  }

  // ── MFA (TOTP) ───────────────────────────────────────────────────
  // Step-up multi-factor: the user enrols here (setup → confirm), then
  // high-risk actions (bank-account change, withdrawal) carry a fresh
  // single-use code. The shared secret is stored encrypted server-side;
  // the FE only ever sees the provisioning URI / QR during enrolment.

  /** Whether the caller has a confirmed TOTP enrolment. */
  async getMfaStatus(): Promise<MfaStatus> {
    const res = await this.api.get<MfaStatus>('/api/v1/auth/mfa/status');
    return res.data;
  }

  /** Begin enrolment: returns the provisioning URI + QR to scan. The
   *  secret is persisted encrypted-but-unconfirmed until `confirmMfa`
   *  validates a live code. 409 if already enrolled (reset is admin-only). */
  async setupMfa(): Promise<MfaSetup> {
    const res = await this.api.post<MfaSetup>('/api/v1/auth/mfa/setup');
    return res.data;
  }

  /** Finish enrolment by confirming the first live 6-digit code. Returns
   *  the freshly-minted recovery codes — plaintext, shown to the user
   *  exactly ONCE (not retrievable afterwards). */
  async confirmMfa(code: string): Promise<MfaConfirmResult> {
    const res = await this.api.post<MfaConfirmResult>('/api/v1/auth/mfa/confirm', { code });
    return res.data;
  }

  /** Mint a fresh set of recovery codes, invalidating the old set. A live
   *  TOTP step-up `mfa_code` is required, EXCEPT in the narrow window right
   *  after a recovery-code login (where the backend allows omitting it).
   *  Returns the new codes — shown once. */
  async regenerateRecoveryCodes(mfaCode?: string): Promise<MfaConfirmResult> {
    const res = await this.api.post<MfaConfirmResult>(
      '/api/v1/auth/mfa/recovery-codes/regenerate',
      mfaCode ? { mfa_code: mfaCode } : {},
    );
    return res.data;
  }

  // ── WebAuthn (passkeys) ──────────────────────────────────────────────
  // Registration ceremony (requires a TOTP step-up code), listing, and
  // removal. Login-time assertion options live on getWebAuthnLoginOptions.

  /** Begin passkey registration: returns `PublicKeyCredentialCreationOptions`
   *  (base64url JSON). Feed through `preformatCreationOptions` before
   *  `navigator.credentials.create()`. */
  async getWebAuthnRegisterOptions(mfaCode: string): Promise<Record<string, unknown>> {
    const res = await this.api.post<Record<string, unknown>>(
      '/api/v1/auth/webauthn/register/options',
      { mfa_code: mfaCode },
    );
    return res.data;
  }

  /** Finish passkey registration with the serialized attestation credential
   *  and a human label. */
  async verifyWebAuthnRegistration(
    credential: Record<string, unknown>,
    label: string,
  ): Promise<WebAuthnCredential> {
    const res = await this.api.post<WebAuthnCredential>(
      '/api/v1/auth/webauthn/register/verify',
      { credential, label },
    );
    return res.data;
  }

  /** List the caller's registered passkeys. */
  async listWebAuthnCredentials(): Promise<WebAuthnCredential[]> {
    const res = await this.api.get<WebAuthnCredential[]>(
      '/api/v1/auth/webauthn/credentials',
    );
    return res.data;
  }

  /** Remove a passkey. Requires a fresh TOTP step-up `mfa_code`, carried in
   *  the body (never the URL) so it stays out of logs and history. */
  async removeWebAuthnCredential(id: string, mfaCode: string): Promise<void> {
    await this.api.post(
      `/api/v1/auth/webauthn/credentials/${id}/remove`,
      { mfa_code: mfaCode },
    );
  }

  /** The sign-in providers this environment offers, in display order.
   *  Resolves to an empty list on ANY failure: the login page renders
   *  without them rather than break over a list it could not read. */
  async listSignInProviders(): Promise<SignInProvider[]> {
    try {
      const res = await this.api.get<unknown>('/api/v1/auth/oauth/providers');
      return toSignInProviders(res.data);
    } catch {
      return [];
    }
  }

  /** Ask where to send the browser to sign in with `providerId`. A sign-in
   *  started from an invitation link sends that link's token along — in the
   *  body, never the URL — so redeeming the invitation is part of the
   *  sign-in. Rejects when the provider is not available (404) or the answer
   *  is not a web address; the caller navigates only on success. */
  async startProviderSignIn(
    providerId: string,
    invitationToken?: string,
  ): Promise<string> {
    const res = await this.api.post<{ authorization_url?: unknown }>(
      `/api/v1/auth/oauth/${encodeURIComponent(providerId)}/start`,
      {
        ...(invitationToken ? { invitation_token: invitationToken } : {}),
      },
      // The answer ties the sign-in to this browser with a short-lived cookie,
      // which the browser only keeps when the request is made with credentials.
      { withCredentials: true },
    );
    const url = res.data?.authorization_url;
    // The browser is about to be sent there, so it must be a web address.
    if (typeof url !== 'string' || !/^https?:\/\//i.test(url)) {
      throw new Error('Sign-in start returned no address to continue to');
    }
    return url;
  }

  /** Exchange the single-use OAuth handoff code for the actual
   *  access+refresh JWTs. The code is what the backend put in the
   *  redirect URL after the provider's callback; swapping it
   *  server-side keeps the JWTs out of browser history, Referer
   *  headers, and access logs. Three outcomes: a Token (persisted here),
   *  a second-factor challenge for an account that still owes one
   *  (NOTHING persisted — the caller completes `verifyMfa`), or null on
   *  any failure so the caller can fall back to the generic error.
   */
  async exchangeOAuthCode(code: string): Promise<LoginResult | null> {
    try {
      const response = await axios.post<LoginResult>(
        `${API_URL}/api/v1/auth/oauth/exchange`,
        { code },
      );
      const data = response.data;
      // Same rule as `login()`: a challenge carries NO tokens, so the
      // store must not be written off it.
      if (isMfaChallenge(data)) {
        return data;
      }
      if (!isTokenResponse(data)) {
        return null;
      }
      // Same contract as `login()` — persist through the single
      // store. Nobody typed an email on this path (the backend resolved
      // the account from the provider's answer), so the store takes the
      // address from the token and the caller's /auth/me round-trip
      // fills in the rest of the profile.
      this.persistToken(data, '');
      return data;
    } catch (err) {
      console.error('OAuth exchange failed:', err);
      return null;
    }
  }

  /** The caller's open sessions, most recent sign-in first. */
  async listMySessions(): Promise<ActiveSession[]> {
    const res = await this.api.get<{ sessions: ActiveSession[] }>('/api/v1/auth/sessions');
    return res.data.sessions;
  }

  /** Sign one of the caller's sessions out; it stops working on its next request. */
  async endSession(sessionId: string): Promise<void> {
    await this.api.delete(`/api/v1/auth/sessions/${sessionId}`);
  }

  /** Sign out every session of the caller except the one in use. Returns how many ended. */
  async endOtherSessions(): Promise<number> {
    const res = await this.api.post<{ ended: number }>('/api/v1/auth/sessions/end-others');
    return res.data.ended;
  }

  /** "Don't recognise a session? Secure your account": every session ends (this one too),
   *  a new password is owed and, for a client, the account is put on hold. The device is
   *  signed out locally, since the server has already ended its session. */
  async secureMyAccount(): Promise<string> {
    const res = await this.api.post<{ message: string }>('/api/v1/auth/sessions/secure');
    this.clearLocalSession();
    return res.data.message;
  }

  private clearLocalSession(): void {
    inactivity.clear();
    this._meCache = null;
    this._meInflight = null;
    useAuthStore.getState().logout();
  }

  logout(): void {
    // Capture BOTH tokens before clearing the store. The server call must carry
    // the access token so the backend can (a) deny it immediately on the access
    // denylist and (b) revoke the refresh row. We set the Authorization header
    // explicitly rather than relying on the request interceptor: the store is
    // cleared synchronously below, and the interceptor (a microtask) reads the
    // token from localStorage AFTER that clear — so without the explicit header
    // the POST would race out unauthenticated (403) and revoke nothing.
    const { accessToken, refreshToken } = useAuthStore.getState();
    if (refreshToken && accessToken) {
      this.api
        .post(
          '/api/v1/auth/logout',
          { refresh_token: refreshToken },
          { headers: { Authorization: `Bearer ${accessToken}` } },
        )
        .catch(() => undefined);
    }
    // Single clear path — the persist middleware writes the cleared
    // state to localStorage synchronously, so a hard navigation
    // right after this lands on /login with no token residue.
    this.clearLocalSession();
  }

  getToken(): string | null {
    return useAuthStore.getState().accessToken;
  }

  getRole(): string | null {
    return useAuthStore.getState().user?.role ?? null;
  }

  getApi(): AxiosInstance {
    return this.api;
  }
}

export default new AuthService();
